import * as React from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Stack,
  Chip,
  Drawer,
  IconButton,
  Backdrop,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";

import {
  Exercise,
  ExerciseTemplate,
  SetInfo,
  TemplateType,
  SchedulePlan,
} from "../../../../models/types";
import GlassContainer from "../../components/liquid-glass";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import TemplateEditorDrawer from "../../components/drawer-template-create";

/* ---------- Props ---------- */
type TemplateScrollerProps = {
  templates: TemplateType[];
  exercisesList: Exercise[];
  /** Called when user clicks “Ask AI”. Should return a fully populated template. */
  onRequestAITemplate?: (notes: string) => Promise<TemplateType>;
  /** Persist new or edited templates */
  onSaveTemplate?: (template: TemplateType) => Promise<void> | void;
  /** Optional: delete */
  onDeleteTemplate?: (templateId: string) => Promise<void> | void;
  /** Optional: re-fetch fresh templates after writes */
  onRefreshTemplates?: () => Promise<void> | void;
};

/* ---------- Utilities ---------- */
const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

const emptySet = (): SetInfo => ({
  id: uid(),
  previous: "—",
  current: { weight: 0, reps: 0 },
});

const cloneTemplate = (t: TemplateType): TemplateType =>
  JSON.parse(JSON.stringify(t));

const summarizeTemplate = (t: TemplateType) => {
  const totalSets = t.exercises.reduce((s, e) => s + (e.sets?.length || 0), 0);
  const totalExercises = t.exercises.length;
  return { totalSets, totalExercises };
};

const ensureHttp = (u?: string | null) => {
  const v = (u ?? "").trim();
  if (!v) return "";
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};
const openUrl = (u?: string | null) => {
  const href = ensureHttp(u);
  if (!href) return;
  window.open(href, "_blank", "noopener,noreferrer");
};

/* ---------- PlannedUse helpers ---------- */
const makeEmptyPlan = (): SchedulePlan => ({
  startDate: "",
  endDate: "",
  timezone: undefined,
  weekdays: {
    sunday: false,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
  },
});

const hasAnyWeekday = (p?: SchedulePlan | null) =>
  !!p &&
  !!(
    p.weekdays.sunday ||
    p.weekdays.monday ||
    p.weekdays.tuesday ||
    p.weekdays.wednesday ||
    p.weekdays.thursday ||
    p.weekdays.friday ||
    p.weekdays.saturday
  );

const weekdayOrder: (keyof SchedulePlan["weekdays"])[] = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];
const weekdayShort: Record<keyof SchedulePlan["weekdays"], string> = {
  sunday: "Sun",
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
};

const formatPlannedUse = (p?: SchedulePlan | null) => {
  if (!p || !p.startDate) return "Planned: N/A";
  const isRecurring = hasAnyWeekday(p);
  const range =
    p.endDate && p.endDate !== p.startDate
      ? `${p.startDate} → ${p.endDate}`
      : p.startDate;
  if (!isRecurring) return range;
  const days = weekdayOrder
    .filter((k) => p.weekdays[k])
    .map((k) => weekdayShort[k]);
  return `${range} • ${days.join(", ") || "—"}`;
};

/* ---------------- Tokenized search helpers (for editor autocomplete) ---------------- */
type Token =
  | { kind: "term"; value: string }
  | { kind: "phrase"; value: string }
  | { kind: "exclude"; value: string }
  | {
      kind: "field";
      field: "name" | "type" | "mg" | "muscle" | "instr" | "demo";
      value: string;
    };

const normalize = (s?: string | null) =>
  (s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim();

type FieldKey = Extract<Token, { kind: "field" }>["field"];

const fieldAlias = (raw: string): FieldKey | null => {
  const k = raw.toLowerCase();
  if (k === "name" || k === "n") return "name";
  if (k === "type" || k === "t") return "type";
  if (k === "mg" || k === "muscle" || k === "group") return "mg";
  if (k === "instr" || k === "instruction" || k === "instructions")
    return "instr";
  if (k === "demo" || k === "video" || k === "yt") return "demo";
  return null;
};

const parseQuery = (q: string): Token[] => {
  const tokens: Token[] = [];
  const re = /"([^"]+)"|(\S+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(q))) {
    const quoted = m[1];
    const raw = m[2];
    if (quoted) {
      tokens.push({ kind: "phrase", value: normalize(quoted) });
    } else if (raw) {
      const fv = raw.split(":", 2);
      if (fv.length === 2) {
        const f = fieldAlias(fv[0]);
        if (f) {
          const val = normalize(fv[1]);
          if (val) tokens.push({ kind: "field", field: f, value: val });
          continue;
        }
      }
      if (raw.startsWith("-")) {
        const v = normalize(raw.slice(1));
        if (v) tokens.push({ kind: "exclude", value: v });
      } else {
        const v = normalize(raw);
        if (v) tokens.push({ kind: "term", value: v });
      }
    }
  }
  return tokens;
};

const exerciseSearchText = (e: Exercise) => {
  const parts: string[] = [];
  parts.push(e.name ?? "");
  parts.push(e.type ?? "");
  parts.push(e.muscleGroup ?? "");
  parts.push(...(e.detailedMusclesImpacted ?? []));
  parts.push(...(e.Instructions ?? []));
  parts.push(e.shortYouTubeDemonstration ?? "");
  parts.push(e.inDepthYouTubeExplanation ?? "");
  return normalize(parts.filter(Boolean).join(" "));
};

type MatchResult = { ok: boolean; score: number; nameHits: number };

const matchExercise = (e: Exercise, tokens: Token[]): MatchResult => {
  if (!tokens.length) return { ok: true, score: 0, nameHits: 0 };

  const text = exerciseSearchText(e);
  const nName = normalize(e.name);
  const nType = normalize(e.type);
  const nMg = normalize(e.muscleGroup);
  const nInstr = normalize((e.Instructions ?? []).join(" "));
  const nDemo = normalize(
    [e.shortYouTubeDemonstration, e.inDepthYouTubeExplanation]
      .filter(Boolean)
      .join(" ")
  );

  let score = 0;
  let nameHits = 0;

  for (const t of tokens) {
    if (t.kind === "exclude") {
      if (text.includes(t.value)) return { ok: false, score: 0, nameHits: 0 };
      continue;
    }

    if (t.kind === "field") {
      const hit =
        (t.field === "name" && nName.includes(t.value)) ||
        (t.field === "type" && nType.includes(t.value)) ||
        (t.field === "mg" && nMg.includes(t.value)) ||
        (t.field === "instr" && nInstr.includes(t.value)) ||
        (t.field === "demo" && nDemo.includes(t.value));
      if (!hit) return { ok: false, score: 0, nameHits: 0 };

      if (t.field === "name") {
        nameHits += 1;
        score += 5;
      }
      continue;
    }

    if (!nName.includes(t.value)) return { ok: false, score: 0, nameHits: 0 };

    nameHits += 1;
    score += 5;
  }

  if (nName)
    score += Math.max(0, 3 - Math.min(3, Math.floor(nName.length / 10)));

  return { ok: true, score, nameHits };
};

/* ---------- Main Component ---------- */
const TemplateScroller: React.FC<TemplateScrollerProps> = ({
  templates,
  exercisesList,
  onRequestAITemplate,
  onSaveTemplate,
  onDeleteTemplate,
  onRefreshTemplates,
}) => {
  // local list shown in scroller
  const [localTemplates, setLocalTemplates] = React.useState<TemplateType[]>(
    templates ?? []
  );
  React.useEffect(() => {
    setLocalTemplates(templates ?? []);
  }, [templates]);

  // two drawers: vault attach (left) and editor (right)
  const [vaultOpen, setVaultOpen] = React.useState(false);
  const [editorOpen, setEditorOpen] = React.useState(false);

  // editor draft
  const [draft, setDraft] = React.useState<TemplateType | null>(null);

  // busy flag (used for saving/attaching)
  const [busy, setBusy] = React.useState(false);

  const darkMode = useSelector((state: RootState) => state.partners.darkMode);
  const partnerData = useSelector(
    (state: RootState) => state.partners.partnerData?.partnerData
  );

  /* --- actions --- */
  const openManualCreate = () => {
    setDraft({
      id: uid(),
      title: "New Template",
      exercises: [],
      dateLastUsed: "",
      plannedUse: undefined,
      difficulty: 3,
    });
    setEditorOpen(true);
  };

  const openEditorWith = (t: TemplateType) => {
    setDraft(cloneTemplate(t));
    setEditorOpen(true);
  };

  const handleSave = async (t: TemplateType) => {
    setBusy(true);
    try {
      await onSaveTemplate?.(t);
      upsertLocal(t);
      setEditorOpen(false);
      await postWriteRefresh();
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!onDeleteTemplate) return;
    setBusy(true);
    try {
      await onDeleteTemplate(id);
      removeLocal(id);
      setEditorOpen(false);
      await postWriteRefresh();
    } finally {
      setBusy(false);
    }
  };

  const upsertLocal = (t: TemplateType) => {
    setLocalTemplates((prev) => {
      const idx = prev.findIndex((x) => x.id === t.id);
      if (idx === -1) return [t, ...prev];
      const copy = [...prev];
      copy[idx] = t;
      return copy;
    });
  };

  const removeLocal = (id: string) => {
    setLocalTemplates((prev) => prev.filter((x) => x.id !== id));
  };

  const postWriteRefresh = async () => {
    try {
      await onRefreshTemplates?.();
    } catch {
      // optimistic-only if refresh fails
    }
  };

  // Attach (quick-apply) an existing vault template to this client
  const handleAttachFromVault = async (t: TemplateType) => {
    // create a new copy when attaching
    const copy = cloneTemplate(t);
    copy.id = uid();
    copy.dateLastUsed = "";
    setBusy(true);
    try {
      await onSaveTemplate?.(copy);
      upsertLocal(copy);
      setVaultOpen(false);
      await postWriteRefresh();
    } finally {
      setBusy(false);
    }
  };

  /* ---------- Autocomplete filter for the shared editor ---------- */
  const filterExerciseOptions = React.useCallback(
    (options: Exercise[], state: { inputValue: string }) => {
      const rawInput = state.inputValue || "";
      const tokens = parseQuery(rawInput);

      if (!rawInput.trim()) {
        return [...options].sort((a, b) =>
          (a?.name || "").localeCompare(b?.name || "", undefined, {
            sensitivity: "base",
            numeric: true,
          })
        );
      }

      return options
        .map((ex) => ({ ex, mr: matchExercise(ex, tokens) }))
        .filter(({ mr }) => mr.ok)
        .sort((a, b) => b.mr.score - a.mr.score)
        .map(({ ex }) => ex);
    },
    []
  );

  /* ---------- UI helpers ---------- */
  const { totalExercises, totalSets } = React.useMemo(
    () =>
      draft ? summarizeTemplate(draft) : { totalExercises: 0, totalSets: 0 },
    [draft]
  );

  return (
    <Box>
      {/* Horizontal scroller */}
      <Box
        sx={{
          display: "flex",
          gap: 1.25,
          overflowX: "auto",
          py: 1,
          px: 0.5,
          scrollSnapType: "x proximity",
          opacity: busy ? 0.6 : 1,
          pointerEvents: busy ? "none" : "auto",
        }}
      >
        {/* Create Template (Left click = Attach from Vault • Right click = Manual) */}
        <GlassContainer>
          <Card
            variant="outlined"
            sx={{
              minWidth: 220,
              height: 120,
              flex: "0 0 auto",
              borderStyle: "dashed",
              scrollSnapAlign: "start",
              backgroundColor: "transparent",
              border: "0px solid transparent",
              borderRadius: 6,
            }}
          >
            <CardActionArea
              sx={{ height: "100%", backgroundColor: "transparent" }}
              title="Left click: Attach from Vault • Right click: Manual"
              onClick={() => setVaultOpen(true)}
              onContextMenu={(e) => {
                e.preventDefault();
                openManualCreate();
              }}
              disabled={busy}
            >
              <CardContent
                sx={{
                  height: "100%",
                  display: "grid",
                  placeItems: "center",
                  textAlign: "center",
                }}
              >
                <AddIcon />
                <Typography sx={{ fontWeight: 700 }}>Add a template</Typography>
                <Typography variant="caption" color="text.secondary">
                  Left: Vault --- Right: Manual
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </GlassContainer>

        {/* Existing templates */}
        {localTemplates.map((t) => {
          const { totalExercises, totalSets } = summarizeTemplate(t);
          return (
            <GlassContainer key={t.id}>
              <Card
                variant="outlined"
                sx={{
                  minWidth: 260,
                  height: 120,
                  flex: "0 0 auto",
                  scrollSnapAlign: "start",
                  backgroundColor: "transparent",
                  border: "0px solid transparent",
                  borderRadius: 6,
                }}
              >
                <CardActionArea
                  onClick={() => openEditorWith(t)}
                  sx={{ height: "100%", backgroundColor: "transparent" }}
                  disabled={busy}
                >
                  <CardContent
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 800, mb: 0.5 }}
                    >
                      {t.title}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip
                        size="small"
                        icon={<FitnessCenterIcon />}
                        label={`${totalExercises} ex • ${totalSets} sets`}
                        variant="outlined"
                        color={darkMode ? "secondary" : "primary"}
                      />
                      <Chip
                        size="small"
                        icon={<CalendarMonthIcon />}
                        label={formatPlannedUse(t.plannedUse)}
                        variant="outlined"
                        color={darkMode ? "secondary" : "primary"}
                      />
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: "auto" }}
                    >
                      Last used:{" "}
                      {t.dateLastUsed
                        ? new Date(t.dateLastUsed).toLocaleString()
                        : "—"}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </GlassContainer>
          );
        })}
      </Box>

      {/* ===================== Vault Attach Drawer (LEFT) ===================== */}
      <Drawer
        anchor="left"
        open={vaultOpen}
        onClose={() => (busy ? null : setVaultOpen(false))}
        PaperProps={{
          sx: { width: { xs: "100%", sm: "70%" }, position: "relative" },
        }}
        ModalProps={{
          keepMounted: true,
          disableScrollLock: true,
          BackdropProps: { sx: { backdropFilter: "blur(6px)" } },
        }}
      >
        {/* Busy overlay inside drawer */}
        <Backdrop
          open={busy}
          sx={{
            color: "#fff",
            position: "absolute",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        <Box
          sx={{
            height: "100dvh",
            background: darkMode
              ? "linear-gradient(135deg, #1B3C34 0%, #2A4B3F 100%)"
              : "linear-gradient(135deg, #FFFFFF 0%, #F5F7FA 100%)",
            color: darkMode ? "#E0E0E0" : "#333333",
            overflowY: "auto",
            pb: "env(safe-area-inset-bottom)",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              px: 2,
              py: 1.25,
              borderBottom: "1px solid",
              borderColor: darkMode ? "#4A6B5A" : "#E0E0E0",
              bgcolor: darkMode ? "#1B3C34" : "#FFFFFF",
              display: "flex",
              alignItems: "center",
              gap: 1,
              backdropFilter: "saturate(1.2) blur(4px)",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                flex: 1,
                color: darkMode ? "#FFFFFF" : "#333333",
              }}
            >
              Attach from Vault
            </Typography>
            <IconButton
              size="small"
              onClick={() => setVaultOpen(false)}
              disabled={busy}
              sx={{ color: darkMode ? "#E0E0E0" : "#333333" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Vault grid */}
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: 1,
              }}
            >
              {(partnerData?.templates ?? []).map((t: TemplateType) => {
                const { totalExercises, totalSets } = summarizeTemplate(t);
                return (
                  <GlassContainer key={`vault-${t.id}`}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: 120,
                        backgroundColor: "transparent",
                        border: "0px solid transparent",
                        borderRadius: 6,
                      }}
                    >
                      <CardActionArea
                        onClick={() => handleAttachFromVault(t)}
                        title="Attach this template to the client"
                        sx={{ height: "100%", backgroundColor: "transparent" }}
                        disabled={busy}
                      >
                        <CardContent
                          sx={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 800, mb: 0.5 }}
                            noWrap
                          >
                            {t.title}
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Chip
                              size="small"
                              icon={<FitnessCenterIcon />}
                              label={`${totalExercises} ex • ${totalSets} sets`}
                              variant="outlined"
                              color={darkMode ? "secondary" : "primary"}
                            />
                            <Chip
                              size="small"
                              icon={<CalendarMonthIcon />}
                              label={formatPlannedUse(t.plannedUse)}
                              variant="outlined"
                              color={darkMode ? "secondary" : "primary"}
                            />
                          </Stack>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: "auto" }}
                            noWrap
                          >
                            Last used:{" "}
                            {t.dateLastUsed
                              ? new Date(t.dateLastUsed).toLocaleString()
                              : "—"}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </GlassContainer>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* ===================== Manual Editor Drawer (RIGHT - shared) ===================== */}
      <TemplateEditorDrawer
        open={editorOpen}
        anchor="right"
        draft={draft ?? undefined}
        onChange={(t) => setDraft(t)}
        exercises={exercisesList}
        darkMode={darkMode}
        onSave={handleSave}
        onDelete={onDeleteTemplate ? (id) => handleDelete(id) : undefined}
        onClose={() => setEditorOpen(false)}
        // pass the same ranked filter used before
        filterExerciseOptions={filterExerciseOptions}
      />
    </Box>
  );
};

export default TemplateScroller;
